// Names the API / validators put in their messages, as the customer should read them.
const FIELD_LABELS: Record<string, string> = {
  billing_address_detail: 'Billing address details',
  billing_address_details: 'Billing address details',
  shipping_address_detail: 'Shipping address details',
  shipping_address_details: 'Shipping address details',
  billing_address: 'Billing address',
  shipping_address: 'Shipping address',
  shipping_ffl: 'Preferred FFL',
  payment_method: 'Payment method',
  cart_item_ids: 'Cart items',
  coupon_code: 'Coupon code',
};

/** "first_name" / "billing_address.state" -> "First name" / "Billing address state". */
export const humanizeFieldName = (name: string) => {
  const text = name.replace(/[_.]+/g, ' ').trim().toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// A lower-case snake_case name ("billing_address_detail"), optionally quoted, optionally followed by a
// dotted path ("billing_address_detail.state"). The look-arounds keep it off emails, URLs, file names
// and anything glued to other text; upper-case tokens (coupon / order codes like SAVE_10) never match.
const FIELD_NAME_PATTERN =
  /(?<![\w@./:#-])["']?([a-z][a-z0-9]*(?:_[a-z0-9]+)+)((?:\.[a-z][a-z0-9_]*)*)["']?(?![\w@])/g;

/**
 * Turns an API / validation message that quotes raw field names ("billing_address_details is
 * required", '"billing_address_detail.state" must not be empty') into plain wording
 * ("Billing address details is required"). Messages without such names come back untouched,
 * so it is safe to run on every error message.
 */
export const getFriendlyFieldMessage = (message?: string | null, fallback = ''): string => {
  if (!message || typeof message !== 'string') return fallback;
  let changed = false;
  let text = message.replace(FIELD_NAME_PATTERN, (match, name: string, path: string) => {
    const known = FIELD_LABELS[name];
    // a dotted tail on a name we don't know is far more likely a file name or domain - leave it
    if (path && !known) return match;
    changed = true;
    const base = known ?? humanizeFieldName(name);
    if (!path) return base;
    const nested = `${base} ${humanizeFieldName(path)}`.replace(/ +/g, ' ').toLowerCase();
    return nested.charAt(0).toUpperCase() + nested.slice(1);
  });
  if (!changed) return message;
  // every flavour of "missing" reads the same: "<Field> is required"
  text = text.replace(
    /\b(?:is|are) (?:a required field|not allowed to be empty|missing|requested|required)\b/i,
    'is required'
  );
  return text.charAt(0).toUpperCase() + text.slice(1);
};
